import {getEnumFromObjectKeys} from '@packages/utilities/zod';
import {type WorkspaceTokenResourcePermissions} from './workspaceToken';

export const WORKSPACE_ROLES = {
  owner: 'owner',
  admin: 'admin',
  member: 'member',
  guest: 'guest',
  commenter: 'commenter',
  public: 'public',
  client: 'client',
} as const;

export const workspaceRoles = getEnumFromObjectKeys(WORKSPACE_ROLES);

export const LEGACY_WORKSPACE_ROLE = WORKSPACE_ROLES.admin;

export type WorkspaceRolesType = keyof typeof WORKSPACE_ROLES;

// @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type '("commenter" | "public" | "owner" | "admin" | "member" | "guest")[]'.
export const WORKSPACE_ROLE_KEYS: Array<WorkspaceRolesType> =
  Object.keys(WORKSPACE_ROLES);

export type WorkspaceResourcePermissions = Readonly<{
  read: boolean;
  update: boolean;
  delete: boolean;
}>;
export type WorkspaceSiteRolesResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;
export type WorkspaceInviteResourcePermissions = Readonly<{
  create: boolean;
  update: boolean;
  delete: boolean;
  createReviewer: boolean;
}>;
export type WorkspaceLibraryResourcePermissions = Readonly<{
  manage: boolean;
}>;
export type WorkspaceMembershipResourcePermissions = Readonly<{
  read: boolean;
  update: boolean;
  deleteOwn: boolean;
}>;
export type WorkspaceFolderResourcePermissions = Readonly<{
  read: boolean;
  update: boolean;
}>;
export type WorkspaceSiteResourcePermissions = Readonly<{
  create: boolean;
  update: boolean;
  read: boolean;
  manageMembership: boolean;
  readCredited: boolean;
  moderateComments: boolean;
  restrictAccess: boolean;
  // User can read sites they have been granted explicit access to.
  // Preserves SSA behavior when SSA is disabled in a workspace or site.
  // Used with `workspaceSite.read:false`
  readRequiresExplicitAccess: boolean;
}>;
export type WorkspaceIntegrationResourcePermissions = Readonly<{
  read: boolean;
  update: boolean;
  install: boolean;
}>;
export type WorkspaceProfilePermissions = Readonly<{
  update: boolean;
}>;

export type WorkspaceAppResourcePermissions = Readonly<{
  install: boolean;
}>;

export type WorkspacePartnerResourceGroupPermissions = Readonly<{
  read: boolean;
}>;

export type WorkspacePartnerResourcePermissions = Readonly<{
  read: boolean;
}>;

export type WorkspacePartnerCommissionPermissions = Readonly<{
  read: boolean;
}>;

export type WorkspaceBillingResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;

export type WorkspaceClientResourcePermissions = Readonly<{
  read: boolean;
}>;

export type WorkspacePartnerTermsOfServicePermissions = Readonly<{
  update: boolean;
}>;

export type WorkspacePartnerProgramPermissions = Readonly<{
  manage: boolean;
  takeExam: boolean;
}>;

export type WorkspaceOptimizeNonHostedSitePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;

export type WorkspaceAgenticWorkflowsResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;

export type WorkspaceAssetResourcePermissions = Readonly<{
  read: boolean;
  manage: boolean;
}>;

export type WorkspaceAuditLogResourcePermissions = Readonly<{
  read: boolean;
}>;

export type WorkspacePermissions = Readonly<{
  workspace: WorkspaceResourcePermissions;
  workspaceToken: WorkspaceTokenResourcePermissions;
  workspaceSiteRoles: WorkspaceSiteRolesResourcePermissions;
  workspaceInvite: WorkspaceInviteResourcePermissions;
  workspaceMembership: WorkspaceMembershipResourcePermissions;
  workspaceFolder: WorkspaceFolderResourcePermissions;
  workspaceSite: WorkspaceSiteResourcePermissions;
  workspaceIntegration: WorkspaceIntegrationResourcePermissions;
  workspaceLibrary: WorkspaceLibraryResourcePermissions;
  workspaceProfile: WorkspaceProfilePermissions;
  workspaceApp: WorkspaceAppResourcePermissions;
  workspaceBilling: WorkspaceBillingResourcePermissions;
  workspaceClient: WorkspaceClientResourcePermissions;
  workspacePartnerResourceGroup: WorkspacePartnerResourceGroupPermissions;
  workspacePartnerResources: WorkspacePartnerResourcePermissions;
  workspacePartnerCommissions: WorkspacePartnerCommissionPermissions;
  workspacePartnerTermsOfService: WorkspacePartnerTermsOfServicePermissions;
  workspacePartnerProgram: WorkspacePartnerProgramPermissions;
  workspaceOptimizeNonHostedSite: WorkspaceOptimizeNonHostedSitePermissions;
  workspaceAgenticWorkflows: WorkspaceAgenticWorkflowsResourcePermissions;
  workspaceAsset: WorkspaceAssetResourcePermissions;
  workspaceAuditLog: WorkspaceAuditLogResourcePermissions;
}>;

// TODO: switch back to the more generic WorkspaceACL type once we can
// import packages in the dashboard! for now, exporting the exact
// type with "true" or "false" instead of "boolean" means any
// changes to the ACL here which are not also implemented in
// the dashboard will be caught by flow

// type WorkspaceACL = $ReadOnly<{
//   [WorkspaceRolesType]: WorkspacePermissions,
// }>;

export type WorkspaceACL = Readonly<{
  owner: {
    workspace: {
      read: true;
      update: true;
      delete: true;
    };
    workspaceToken: {
      read: true;
      create: true;
      delete: true;
      update: true;
    };
    workspaceSiteRoles: {
      read: true;
      manage: true;
    };
    workspaceInvite: {
      create: true;
      update: true;
      delete: true;
      createReviewer: true;
    };
    workspaceMembership: {
      read: true;
      update: true;
      deleteOwn: false;
    };
    workspaceFolder: {
      read: true;
      update: true;
    };
    workspaceSite: {
      create: true;
      update: true;
      read: true;
      manageMembership: true;
      readCredited: true;
      moderateComments: true;
      restrictAccess: true;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: true;
      update: true;
      install: true;
    };
    workspaceLibrary: {
      manage: true;
    };
    // SP Success permissions
    workspaceProfile: {
      update: true;
    };
    workspacePartnerResourceGroup: {
      read: true;
    };
    workspacePartnerResources: {
      read: true;
    };
    workspacePartnerCommissions: {
      read: true;
    };
    workspaceApp: {
      install: true;
    };
    workspaceBilling: {
      manage: true;
      read: true;
    };
    workspacePartnerTermsOfService: {
      update: true;
    };
    workspacePartnerProgram: {
      manage: true;
      takeExam: true;
    };
    workspaceClient: {
      read: true;
    };
    workspaceOptimizeNonHostedSite: {
      read: true;
      manage: true;
    };
    workspaceAsset: {
      read: true;
      manage: true;
    };
    workspaceAgenticWorkflows: {
      read: true;
      manage: true;
    };
    workspaceAuditLog: {
      read: true;
    };
  };
  admin: {
    workspace: {
      read: true;
      update: true;
      delete: false;
    };
    workspaceToken: {
      read: true;
      create: true;
      delete: true;
      update: true;
    };
    workspaceSiteRoles: {
      read: true;
      manage: true;
    };
    workspaceInvite: {
      create: true;
      update: true;
      delete: true;
      createReviewer: true;
    };
    workspaceMembership: {
      read: true;
      update: true;
      deleteOwn: true;
    };
    workspaceFolder: {
      read: true;
      update: true;
    };
    workspaceSite: {
      create: true;
      update: true;
      read: true;
      manageMembership: true;
      readCredited: true;
      moderateComments: true;
      restrictAccess: true;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: true;
      update: true;
      install: true;
    };
    workspaceLibrary: {
      manage: true;
    };
    // SP Success permissions
    workspaceProfile: {
      update: true;
    };
    workspacePartnerResourceGroup: {
      read: true;
    };
    workspacePartnerResources: {
      read: true;
    };
    workspacePartnerCommissions: {
      read: true;
    };
    workspaceApp: {
      install: true;
    };
    workspaceBilling: {
      manage: true;
      read: true;
    };
    workspacePartnerTermsOfService: {
      update: true;
    };
    workspacePartnerProgram: {
      manage: true;
      takeExam: true;
    };
    workspaceClient: {
      read: true;
    };
    workspaceOptimizeNonHostedSite: {
      read: true;
      manage: true;
    };
    workspaceAsset: {
      read: true;
      manage: true;
    };
    workspaceAgenticWorkflows: {
      read: true;
      manage: true;
    };
    workspaceAuditLog: {
      read: true;
    };
  };
  member: {
    workspace: {
      read: true;
      update: false;
      delete: false;
    };
    workspaceToken: {
      read: false;
      create: false;
      delete: false;
      update: false;
    };
    workspaceSiteRoles: {
      read: true;
      manage: false;
    };
    workspaceInvite: {
      create: false;
      update: false;
      delete: false;
      createReviewer: true;
    };
    workspaceMembership: {
      read: false;
      update: false;
      deleteOwn: true;
    };
    workspaceFolder: {
      read: true;
      update: false;
    };
    workspaceSite: {
      create: true;
      update: false;
      read: true;
      manageMembership: false;
      readCredited: true;
      moderateComments: false;
      restrictAccess: false;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: false;
      update: false;
      install: true;
    };
    workspaceLibrary: {
      manage: false;
    };
    // SP Success permissions
    workspaceProfile: {
      update: false;
    };
    workspacePartnerResourceGroup: {
      read: true;
    };
    workspacePartnerResources: {
      read: true;
    };
    workspacePartnerCommissions: {
      read: true;
    };
    workspaceApp: {
      install: false;
    };
    workspaceBilling: {
      manage: false;
      read: true;
    };
    workspacePartnerTermsOfService: {
      update: true;
    };
    workspacePartnerProgram: {
      manage: false;
      takeExam: true;
    };
    workspaceClient: {
      read: true;
    };
    workspaceOptimizeNonHostedSite: {
      read: true;
      manage: false;
    };
    workspaceAsset: {
      read: true;
      manage: true;
    };
    workspaceAgenticWorkflows: {
      read: true;
      manage: false;
    };
    workspaceAuditLog: {
      read: false;
    };
  };
  guest: {
    workspace: {
      read: true;
      update: false;
      delete: false;
    };
    workspaceToken: {
      read: false;
      create: false;
      delete: false;
      update: false;
    };
    workspaceSiteRoles: {
      read: true;
      manage: false;
    };
    workspaceInvite: {
      create: false;
      update: false;
      delete: false;
      createReviewer: false;
    };
    workspaceMembership: {
      read: false;
      update: false;
      deleteOwn: true;
    };
    workspaceFolder: {
      read: true;
      update: true;
    };
    workspaceSite: {
      create: true;
      update: false;
      read: true;
      manageMembership: false;
      readCredited: false;
      moderateComments: false;
      restrictAccess: false;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: false;
      update: false;
      install: true;
    };
    workspaceLibrary: {
      manage: false;
    };
    // SP Success permissions
    workspaceProfile: {
      update: false;
    };
    workspacePartnerResourceGroup: {
      read: false;
    };
    workspacePartnerResources: {
      read: false;
    };
    workspacePartnerCommissions: {
      read: false;
    };
    workspaceApp: {
      install: false;
    };
    workspaceBilling: {
      manage: false;
      read: false;
    };
    workspacePartnerTermsOfService: {
      update: false;
    };
    workspacePartnerProgram: {
      manage: false;
      takeExam: false;
    };
    workspaceClient: {
      read: false;
    };
    workspaceOptimizeNonHostedSite: {
      read: true;
      manage: false;
    };
    workspaceAsset: {
      read: false;
      manage: false;
    };
    workspaceAgenticWorkflows: {
      read: false;
      manage: false;
    };
    workspaceAuditLog: {
      read: false;
    };
  };
  commenter: {
    workspace: {
      read: true;
      update: false;
      delete: false;
    };
    workspaceToken: {
      read: false;
      create: false;
      delete: false;
      update: false;
    };
    workspaceSiteRoles: {
      read: true;
      manage: false;
    };
    workspaceInvite: {
      create: false;
      update: false;
      delete: false;
      createReviewer: true;
    };
    workspaceMembership: {
      read: false;
      update: false;
      deleteOwn: true;
    };
    workspaceFolder: {
      read: true;
      update: false;
    };
    workspaceSite: {
      create: false;
      update: false;
      read: true;
      manageMembership: false;
      readCredited: false;
      moderateComments: false;
      restrictAccess: false;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: false;
      update: false;
      install: false;
    };
    workspaceLibrary: {
      manage: false;
    };
    // SP Success permissions
    workspaceProfile: {
      update: false;
    };
    workspacePartnerResourceGroup: {
      read: false;
    };
    workspacePartnerResources: {
      read: false;
    };
    workspacePartnerCommissions: {
      read: false;
    };
    workspaceApp: {
      install: false;
    };
    workspaceBilling: {
      manage: false;
      read: false;
    };
    workspacePartnerTermsOfService: {
      update: false;
    };
    workspacePartnerProgram: {
      manage: false;
      takeExam: false;
    };
    workspaceClient: {
      read: false;
    };
    workspaceOptimizeNonHostedSite: {
      read: true;
      manage: false;
    };
    workspaceAsset: {
      read: false;
      manage: false;
    };
    workspaceAgenticWorkflows: {
      read: false;
      manage: false;
    };
    workspaceAuditLog: {
      read: false;
    };
  };
  public: {
    workspace: {
      read: false;
      update: false;
      delete: false;
    };
    workspaceToken: {
      read: false;
      create: false;
      delete: false;
      update: false;
    };
    workspaceSiteRoles: {
      read: false;
      manage: false;
    };
    workspaceInvite: {
      create: false;
      update: false;
      delete: false;
      createReviewer: false;
    };
    workspaceMembership: {
      read: false;
      update: false;
      deleteOwn: false;
    };
    workspaceFolder: {
      read: false;
      update: false;
    };
    workspaceSite: {
      create: false;
      update: false;
      read: false;
      manageMembership: false;
      readCredited: false;
      moderateComments: false;
      restrictAccess: false;
      readRequiresExplicitAccess: false;
    };
    workspaceIntegration: {
      read: false;
      update: false;
      install: false;
    };
    workspaceLibrary: {
      manage: false;
    };
    // SP Success permissions
    workspaceProfile: {
      update: false;
    };
    workspacePartnerResourceGroup: {
      read: false;
    };
    workspacePartnerResources: {
      read: false;
    };
    workspacePartnerCommissions: {
      read: false;
    };
    workspaceApp: {
      install: false;
    };
    workspaceBilling: {
      manage: false;
      read: false;
    };
    workspacePartnerTermsOfService: {
      update: false;
    };
    workspacePartnerProgram: {
      manage: false;
      takeExam: false;
    };
    workspaceClient: {
      read: false;
    };
    workspaceOptimizeNonHostedSite: {
      read: false;
      manage: false;
    };
    workspaceAsset: {
      read: false;
      manage: false;
    };
    workspaceAgenticWorkflows: {
      read: false;
      manage: false;
    };
    workspaceAuditLog: {
      read: false;
    };
  };
  client: {
    workspace: {
      read: true;
      update: false;
      delete: false;
    };
    workspaceToken: {
      read: false;
      create: false;
      delete: false;
      update: false;
    };
    workspaceSiteRoles: {
      read: true;
      manage: false;
    };
    workspaceInvite: {
      create: false;
      update: false;
      delete: false;
      createReviewer: false;
    };
    workspaceMembership: {
      read: false;
      update: false;
      deleteOwn: false;
    };
    workspaceFolder: {
      read: false;
      update: false;
    };
    workspaceSite: {
      create: false;
      update: false;
      read: false;
      manageMembership: false;
      readCredited: false;
      moderateComments: false;
      restrictAccess: false;
      readRequiresExplicitAccess: true;
    };
    workspaceIntegration: {
      read: false;
      update: false;
      install: false;
    };
    workspaceLibrary: {
      manage: false;
    };
    workspaceProfile: {
      update: false;
    };
    workspacePartnerResourceGroup: {
      read: false;
    };
    workspacePartnerResources: {
      read: false;
    };
    workspacePartnerCommissions: {
      read: false;
    };
    workspaceApp: {
      install: false;
    };
    workspaceBilling: {
      manage: false;
      read: false;
    };
    workspacePartnerTermsOfService: {
      update: false;
    };
    workspacePartnerProgram: {
      manage: false;
      takeExam: false;
    };
    workspaceClient: {
      read: false;
    };
    workspaceOptimizeNonHostedSite: {
      read: false;
      manage: false;
    };
    workspaceAsset: {
      read: false;
      manage: false;
    };
    workspaceAgenticWorkflows: {
      read: false;
      manage: false;
    };
    workspaceAuditLog: {
      read: false;
    };
  };
}>;

export const WORKSPACE_ACL: WorkspaceACL = {
  owner: {
    workspace: {
      read: true,
      update: true,
      delete: true,
    },
    workspaceToken: {
      read: true,
      create: true,
      delete: true,
      update: true,
    },
    workspaceSiteRoles: {
      read: true,
      manage: true,
    },
    workspaceInvite: {
      create: true,
      update: true,
      delete: true,
      createReviewer: true,
    },
    workspaceMembership: {
      read: true,
      update: true,
      deleteOwn: false,
    },
    workspaceFolder: {
      read: true,
      update: true,
    },
    workspaceSite: {
      create: true,
      update: true,
      read: true,
      manageMembership: true,
      readCredited: true,
      moderateComments: true,
      restrictAccess: true,
      readRequiresExplicitAccess: false,
    },
    workspaceIntegration: {
      read: true,
      update: true,
      install: true,
    },
    workspaceLibrary: {
      manage: true,
    },
    // SP Success permissions
    workspaceProfile: {
      update: true,
    },
    workspacePartnerResourceGroup: {
      read: true,
    },
    workspacePartnerResources: {
      read: true,
    },
    workspacePartnerCommissions: {
      read: true,
    },
    workspaceApp: {
      install: true,
    },
    workspaceBilling: {
      read: true,
      manage: true,
    },
    workspacePartnerTermsOfService: {
      update: true,
    },
    workspacePartnerProgram: {
      manage: true,
      takeExam: true,
    },
    workspaceClient: {
      read: true,
    },
    workspaceOptimizeNonHostedSite: {
      read: true,
      manage: true,
    },
    workspaceAsset: {
      read: true,
      manage: true,
    },
    workspaceAgenticWorkflows: {
      read: true,
      manage: true,
    },
    workspaceAuditLog: {
      read: true,
    },
  },
  admin: {
    workspace: {
      read: true,
      update: true,
      delete: false,
    },
    workspaceToken: {
      read: true,
      create: true,
      delete: true,
      update: true,
    },
    workspaceSiteRoles: {
      read: true,
      manage: true,
    },
    workspaceInvite: {
      create: true,
      update: true,
      delete: true,
      createReviewer: true,
    },
    workspaceMembership: {
      read: true,
      update: true,
      deleteOwn: true,
    },
    workspaceFolder: {
      read: true,
      update: true,
    },
    workspaceSite: {
      create: true,
      update: true,
      read: true,
      manageMembership: true,
      readCredited: true,
      moderateComments: true,
      restrictAccess: true,
      readRequiresExplicitAccess: false,
    },
    workspaceIntegration: {
      read: true,
      update: true,
      install: true,
    },
    workspaceLibrary: {
      manage: true,
    },
    // SP Success permissions
    workspaceProfile: {
      update: true,
    },
    workspacePartnerResourceGroup: {
      read: true,
    },
    workspacePartnerResources: {
      read: true,
    },
    workspacePartnerCommissions: {
      read: true,
    },
    workspaceApp: {
      install: true,
    },
    workspaceBilling: {
      read: true,
      manage: true,
    },
    workspacePartnerTermsOfService: {
      update: true,
    },
    workspacePartnerProgram: {
      manage: true,
      takeExam: true,
    },
    workspaceClient: {
      read: true,
    },
    workspaceOptimizeNonHostedSite: {
      read: true,
      manage: true,
    },
    workspaceAsset: {
      read: true,
      manage: true,
    },
    workspaceAgenticWorkflows: {
      read: true,
      manage: true,
    },
    workspaceAuditLog: {
      read: true,
    },
  },
  member: {
    workspace: {
      read: true,
      update: false,
      delete: false,
    },
    workspaceToken: {
      read: false,
      create: false,
      delete: false,
      update: false,
    },
    workspaceSiteRoles: {
      read: true,
      manage: false,
    },
    workspaceInvite: {
      create: false,
      update: false,
      delete: false,
      createReviewer: true,
    },
    workspaceMembership: {
      read: false,
      update: false,
      deleteOwn: true,
    },
    workspaceFolder: {
      read: true,
      update: false,
    },
    workspaceSite: {
      create: true,
      update: false,
      read: true,
      manageMembership: false,
      readCredited: true,
      moderateComments: false,
      restrictAccess: false,
      readRequiresExplicitAccess: false,
    },
    workspaceIntegration: {
      read: false,
      update: false,
      install: true,
    },
    workspaceLibrary: {
      manage: false,
    },
    // SP Success permissions
    workspaceProfile: {
      update: false,
    },
    workspacePartnerResourceGroup: {
      read: true,
    },
    workspacePartnerResources: {
      read: true,
    },
    workspacePartnerCommissions: {
      read: true,
    },
    workspaceApp: {
      install: false,
    },
    workspaceBilling: {
      read: true,
      manage: false,
    },
    workspacePartnerTermsOfService: {
      update: true,
    },
    workspacePartnerProgram: {
      manage: false,
      takeExam: true,
    },
    workspaceClient: {
      read: true,
    },
    workspaceOptimizeNonHostedSite: {
      read: true,
      manage: false,
    },
    workspaceAsset: {
      read: true,
      manage: true,
    },
    workspaceAgenticWorkflows: {
      read: true,
      manage: false,
    },
    workspaceAuditLog: {
      read: false,
    },
  },
  guest: {
    workspace: {
      read: true,
      update: false,
      delete: false,
    },
    workspaceToken: {
      read: false,
      create: false,
      delete: false,
      update: false,
    },
    workspaceSiteRoles: {
      read: true,
      manage: false,
    },
    workspaceInvite: {
      create: false,
      update: false,
      delete: false,
      createReviewer: false,
    },
    workspaceMembership: {
      read: false,
      update: false,
      deleteOwn: true,
    },
    workspaceFolder: {
      read: true,
      update: true,
    },
    workspaceSite: {
      create: true,
      update: false,
      read: true,
      manageMembership: false,
      readCredited: false,
      moderateComments: false,
      restrictAccess: false,
      readRequiresExplicitAccess: false,
    },
    workspaceIntegration: {
      read: false,
      update: false,
      install: true,
    },
    workspaceLibrary: {
      manage: false,
    },
    // SP Success permissions
    workspaceProfile: {
      update: false,
    },
    workspacePartnerResourceGroup: {
      read: false,
    },
    workspacePartnerResources: {
      read: false,
    },
    workspacePartnerCommissions: {
      read: false,
    },
    workspaceApp: {
      install: false,
    },
    workspaceBilling: {
      read: false,
      manage: false,
    },
    workspacePartnerTermsOfService: {
      update: false,
    },
    workspacePartnerProgram: {
      manage: false,
      takeExam: false,
    },
    workspaceClient: {
      read: false,
    },
    workspaceOptimizeNonHostedSite: {
      read: true,
      manage: false,
    },
    workspaceAsset: {
      read: false,
      manage: false,
    },
    workspaceAgenticWorkflows: {
      read: false,
      manage: false,
    },
    workspaceAuditLog: {
      read: false,
    },
  },
  commenter: {
    workspace: {
      read: true,
      update: false,
      delete: false,
    },
    workspaceToken: {
      read: false,
      create: false,
      delete: false,
      update: false,
    },
    workspaceSiteRoles: {
      read: true,
      manage: false,
    },
    workspaceInvite: {
      create: false,
      update: false,
      delete: false,
      createReviewer: true,
    },
    workspaceMembership: {
      read: false,
      update: false,
      deleteOwn: true,
    },
    workspaceFolder: {
      read: true,
      update: false,
    },
    workspaceSite: {
      create: false,
      update: false,
      read: true,
      manageMembership: false,
      readCredited: false,
      moderateComments: false,
      restrictAccess: false,
      readRequiresExplicitAccess: false,
    },
    workspaceApp: {
      install: false,
    },
    workspaceIntegration: {
      read: false,
      update: false,
      install: false,
    },
    workspaceLibrary: {
      manage: false,
    },
    // SP Success permissions
    workspaceProfile: {
      update: false,
    },
    workspacePartnerResourceGroup: {
      read: false,
    },
    workspacePartnerResources: {
      read: false,
    },
    workspacePartnerCommissions: {
      read: false,
    },
    workspaceBilling: {
      read: false,
      manage: false,
    },
    workspacePartnerTermsOfService: {
      update: false,
    },
    workspacePartnerProgram: {
      manage: false,
      takeExam: false,
    },
    workspaceClient: {
      read: false,
    },
    workspaceOptimizeNonHostedSite: {
      read: true,
      manage: false,
    },
    workspaceAsset: {
      read: false,
      manage: false,
    },
    workspaceAgenticWorkflows: {
      read: false,
      manage: false,
    },
    workspaceAuditLog: {
      read: false,
    },
  },
  public: {
    workspace: {
      read: false,
      update: false,
      delete: false,
    },
    workspaceToken: {
      read: false,
      create: false,
      delete: false,
      update: false,
    },
    workspaceSiteRoles: {
      read: false,
      manage: false,
    },
    workspaceInvite: {
      create: false,
      update: false,
      delete: false,
      createReviewer: false,
    },
    workspaceMembership: {
      read: false,
      update: false,
      deleteOwn: false,
    },
    workspaceFolder: {
      read: false,
      update: false,
    },
    workspaceSite: {
      create: false,
      update: false,
      read: false,
      manageMembership: false,
      readCredited: false,
      moderateComments: false,
      restrictAccess: false,
      readRequiresExplicitAccess: false,
    },
    workspaceIntegration: {
      read: false,
      update: false,
      install: false,
    },
    workspaceLibrary: {
      manage: false,
    },
    // SP Success permissions
    workspaceProfile: {
      update: false,
    },
    workspacePartnerResourceGroup: {
      read: false,
    },
    workspacePartnerResources: {
      read: false,
    },
    workspacePartnerCommissions: {
      read: false,
    },
    workspaceApp: {
      install: false,
    },
    workspaceBilling: {
      read: false,
      manage: false,
    },
    workspacePartnerTermsOfService: {
      update: false,
    },
    workspacePartnerProgram: {
      manage: false,
      takeExam: false,
    },
    workspaceClient: {
      read: false,
    },
    workspaceOptimizeNonHostedSite: {
      read: false,
      manage: false,
    },
    workspaceAsset: {
      read: false,
      manage: false,
    },
    workspaceAgenticWorkflows: {
      read: false,
      manage: false,
    },
    workspaceAuditLog: {
      read: false,
    },
  },
  client: {
    workspace: {
      read: true,
      update: false,
      delete: false,
    },
    workspaceToken: {
      read: false,
      create: false,
      delete: false,
      update: false,
    },
    workspaceSiteRoles: {
      read: true,
      manage: false,
    },
    workspaceInvite: {
      create: false,
      update: false,
      delete: false,
      createReviewer: false,
    },
    workspaceMembership: {
      read: false,
      update: false,
      deleteOwn: false,
    },
    workspaceFolder: {
      read: false,
      update: false,
    },
    workspaceSite: {
      create: false,
      update: false,
      read: false,
      manageMembership: false,
      readCredited: false,
      moderateComments: false,
      restrictAccess: false,
      readRequiresExplicitAccess: true,
    },
    workspaceIntegration: {
      read: false,
      update: false,
      install: false,
    },
    workspaceLibrary: {
      manage: false,
    },
    // SP Success permissions
    workspaceProfile: {
      update: false,
    },
    workspacePartnerResourceGroup: {
      read: false,
    },
    workspacePartnerResources: {
      read: false,
    },
    workspacePartnerCommissions: {
      read: false,
    },
    workspaceApp: {
      install: false,
    },
    workspaceBilling: {
      read: false,
      manage: false,
    },
    workspacePartnerTermsOfService: {
      update: false,
    },
    workspacePartnerProgram: {
      manage: false,
      takeExam: false,
    },
    workspaceClient: {
      read: false,
    },
    workspaceOptimizeNonHostedSite: {
      read: false,
      manage: false,
    },
    workspaceAsset: {
      read: false,
      manage: false,
    },
    workspaceAgenticWorkflows: {
      read: false,
      manage: false,
    },
    workspaceAuditLog: {
      read: false,
    },
  },
};

export function getWorkspacePermissionsByRole(
  role: WorkspaceRolesType
): WorkspacePermissions {
  return WORKSPACE_ACL[role];
}

export function toWorkspaceRole(
  string: string
): WorkspaceRolesType | null | undefined {
  const roleString = string.toLowerCase().trim();
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly owner: "owner"; readonly admin: "admin"; readonly member: "member"; readonly guest: "guest"; readonly commenter: "commenter"; readonly public: "public"; }'.
  return WORKSPACE_ROLES[roleString];
}
